<template>
    <list-container v-if="filteredTasks.length > 0">
        <list-row v-for="task in availableTasks" :key="task.id.toString()">
            <list-cell-action-checkbox
                :model-value="task.status.equals(TaskStatus.COMPLETED)"
                :disabled="task.status.equals(TaskStatus.COMPLETED) || !taskIsEditable(task)"
                @update:model-value="completeTask(task)"
            />

            <list-cell-task-appointment-date
                :date="task.dueDateTime"
                :is-today="task.isToday"
            />

            <list-cell-text style="width: 100%">
                <template #content>
                    <flex-container
                        align-content="flex-end"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon
                            :icon="getIcon(task)"
                            :size="IconSize.sm"
                            :color="getIconColor(task)"
                        />
                        <text-decorator :size="FontSize.sm" bold>
                            {{ task.typeLabel ?? '-' }}
                        </text-decorator>
                        <svg-icon
                            v-if="task.source === 'realworks'"
                            :icon="IconRealworks"
                            :size="IconSize.sm"
                            is-custom-icon
                        />
                    </flex-container>

                    <flex-container align-items="center" :gap="Size.xs">
                        <svg-icon
                            :icon="FilterIcon"
                            :size="IconSize.sm"
                            :color="Color.gray400"
                        />
                        <text-decorator
                            :size="FontSize.sm"
                            :color="Color.gray400"
                        >
                            {{ task.lead.pipeline.name }} &nbsp;&#x2022;
                        </text-decorator>
                        <svg-icon
                            :icon="task.status.equals(TaskStatus.COMPLETED) ? DoneIcon : CircleIcon"
                            :size="IconSize.sm"
                            :color="Color.gray400"
                        />
                        <text-decorator
                            :size="FontSize.sm"
                            :color="Color.gray400"
                        >
                            {{ task.status.label }}
                        </text-decorator>
                    </flex-container>
                </template>
            </list-cell-text>

            <list-cell-text>
                <template #content>
                    <avatar-group>
                        <user-avatar
                            v-for="user in task.assignedUsers"
                            :key="user.id.toString()"
                            :placeholder="user.name.abbreviation"
                            :url="user.avatar ? resizedToSizeUploadUrl(user.avatar, 'lg') : ''"
                            :alt="user.name.fullName"
                        />
                    </avatar-group>
                </template>
            </list-cell-text>

            <list-cell-action-double
                :actions="[{
                    icon: EditIcon,
                    ariaLabel: 'Edit',
                    onClick: () => {
                        router.push(createRouteLocation({
                            name: 'ActionDetail',
                            params: {
                                leadId: task.lead.id.value,
                                actionType: 'task',
                                actionId: task.id.value,
                            },
                        }, 'lead'));
                    },
                    disabled: !taskIsEditable(task),
                }, {
                    icon: CommentIcon,
                    ariaLabel: 'Comment',
                    onClick: () => openCommentModal(task),
                    disabled: !task.comment,
                }]"
            />
        </list-row>
    </list-container>
    <list-message-empty v-else>
        <slot name="empty-message">
            Er zijn geen taken gevonden voor deze filters
        </slot>
    </list-message-empty>
    <list-footer>
        <template #left>
            <slot name="footer-left" :bindings="{ count: filteredTasks.length }" />
        </template>
        <template #default>
            <slot name="footer-center" :bindings="{ count: filteredTasks.length }" />
        </template>
        <template #right>
            <slot name="footer-right" :bindings="{ count: filteredTasks.length }" />
        </template>
    </list-footer>

    <task-appointment-comment-modal
        ref="commentModal"
        :comment="commentModalTask?.comment ?? ''"
    >
        <template v-if="commentModalTask" #subheading>
            <flex-container
                align-content="flex-end"
                align-items="center"
                :gap="Size.xs"
            >
                <svg-icon
                    :icon="getIcon(commentModalTask)"
                    :size="IconSize.sm"
                    :color="getIconColor(commentModalTask)"
                />
                <text-decorator :size="FontSize.sm">
                    {{ commentModalTask.typeLabel ?? '-' }}
                </text-decorator>
                <svg-icon
                    v-if="commentModalTask.source === 'realworks'"
                    :icon="IconRealworks"
                    :size="IconSize.sm"
                    is-custom-icon
                />
            </flex-container>
        </template>
    </task-appointment-comment-modal>
</template>

<script setup lang="ts">
    import {
        Color,
        type ExposedOverlay,
        FontSize,
        ListCellActionCheckbox,
        ListCellActionDouble,
        ListCellText,
        ListContainer,
        ListRow,
        ListMessageEmpty,
        TextDecorator,
        AvatarGroup,
        UserAvatar,
        IconSize,
        Size,
        SvgIcon,
        FlexContainer,
    } from '@keyvue/one-ui';

    import DoneIcon from '@keyvue/one-material-icons/icons/done.svg?component';
    import CircleIcon from '@keyvue/one-material-icons/icons/circle.svg?component';
    import FilterIcon from '@keyvue/one-material-icons/icons/filter_alt.svg?component';
    import MailIcon from '@keyvue/one-material-icons/icons/mail.svg?component';
    import PhoneIcon from '@keyvue/one-material-icons/icons/call.svg?component';
    import NotificationIcon from '@keyvue/one-material-icons/icons/notifications.svg?component';
    import EuroIcon from '@keyvue/one-material-icons/icons/euro.svg?component';
    import UpgradeIcon from '@keyvue/one-material-icons/icons/upgrade.svg?component';

    import IconRealworks from '@/assets/icons/Realworks.svg?component';
    import EditIcon from '@keyvue/one-material-icons/icons/edit.svg?component';
    import CommentIcon from '@keyvue/one-material-icons/icons/sms.svg?component';

    import type { Task } from '@keyvue/one-api-client';
    import { resizedToSizeUploadUrl } from '@/utils/resize';
    import { computed, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import useParentRoute from '@composables/useParentRoute';
    import { TaskStatus, TaskType } from '@keyvue/one-api-client';
    import { ActionAPI } from '@keyvue/one-api-client-legacy';
    import ListCellTaskAppointmentDate from '@components/List/Cells/Actions/ListCellTaskAppointmentDate.vue';
    import TaskAppointmentCommentModal from '@components/Modals/TaskAppointmentCommentModal.vue';
    import useNotify from '@composables/useNotify';
    import useClient from '@composables/useClient';

    const { currentCompany, currentUser } = useClient();
    const updatedTasks = ref<Task[]>([]);

    const props = defineProps<{
        tasks: Task[],
        showAll?: boolean,
        responsibleFilter: 'user' | 'team',
    }>();

    const router = useRouter();
    const createRouteLocation = useParentRoute();
    const { notifySaving, notifySaved } = useNotify();

    const availableTasks = computed(() => (
        props.showAll ? filteredTasks.value : [...filteredTasks.value].splice(0, 4)
    ));

    const filteredTasks = computed(() => {
        if (props.responsibleFilter === 'user') {
            return [...sortedTasks.value].filter(
                (filteredTask) => [...filteredTask.assignedUsers].map(
                    (filterTaskUsers) => filterTaskUsers.id.toString(),
                ).includes(currentUser.value?.id.toString() ?? ''),
            );
        }

        return [...sortedTasks.value].filter(
            (filteredTask) => ![...filteredTask.assignedUsers].map(
                (filterTaskUsers) => filterTaskUsers.id.toString(),
            ).includes(currentUser.value?.id.toString() ?? ''),
        );
    });

    const sortedTasks = computed(() => (
        [...props.tasks].sort((a, b) => ((new Date(b.dueDateTime)).valueOf() - (new Date(a.dueDateTime)).valueOf()))
    ));

    const taskIsEditable = (task: Task) => {
        // Check if task is editable at all
        if (task.source) {
            return false;
        }

        // Check if task is assigned to a user of the current company
        if (task.assignedUsers.length > 0) {
            return task.assignedUsers.some((assignedUser) => {
                if (!assignedUser.company) {
                    return false;
                }

                return assignedUser.company.equals(currentCompany.value);
            });
        }

        // Check if lead is assigned to the current company
        return task.lead.assignee.company.equals(currentCompany.value);
    };

    const completeTask = async (task: Task) => {
        if (updatedTasks.value.find((findableTask) => findableTask.equals(task))) {
            return;
        }

        updatedTasks.value.push(task);

        const completableTask = availableTasks.value.find((a) => a.equals(task));
        if (!completableTask) {
            return;
        }

        completableTask.status = TaskStatus.COMPLETED;

        const closeNotification = notifySaving(undefined, 0);
        const action = await ActionAPI.get(completableTask.id.value);
        action.completed = true;
        await ActionAPI.update(action.id, action);
        notifySaved();
        if (closeNotification) {
            closeNotification();
        }
    };

    const commentModal = ref<ExposedOverlay>();
    const commentModalTask = ref<Task | null>();

    const openCommentModal = (task: Task) => {
        commentModalTask.value = task;
        commentModal.value?.openOverlay();
    };

    /** niet boos worden, eslint wilt het zo */
    const getIcon = (task: Task) => {
        switch (task.type?.value) {
        case TaskType.EMAIL.value:
        case TaskType.LETTER.value:
            return MailIcon;
        case TaskType.CALL.value:
            return PhoneIcon;
        case TaskType.NOTIFICATION.value:
        case TaskType.REMINDER.value:
            return NotificationIcon;
        case TaskType.BILLING.value:
            return EuroIcon;
        case TaskType.UPDATE.value:
            return UpgradeIcon;
        default:
            return '';
        }
    };

    /** niet boos worden, eslint wilt het zo */
    const getIconColor: (task: Task) => Color = (task: Task) => {
        switch (task.type?.value) {
        case TaskType.EMAIL.value:
            return Color.blue500;
        case TaskType.LETTER.value:
            return Color.yellow500;
        case TaskType.CALL.value:
            return Color.brandPurple;
        case TaskType.NOTIFICATION.value:
        case TaskType.REMINDER.value:
            return Color.brandPink;
        case TaskType.BILLING.value:
            return Color.yellow500;
        case TaskType.UPDATE.value:
            return Color.green500;
        default:
            return Color.gray400;
        }
    };
</script>
